import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { Confirm } from '../../../userManagement/components/Confirm';
import { DIAM_GRID_PAGE_SIZE_OPTIONS } from '../../diamGridUtils';
import { useDiamGridPagination } from '../../hooks/useDiamGridPagination';
import { DiamDataGrid } from '../DiamDataGrid/DiamDataGrid';
import { DataGridContainer } from '../DiamDataGrid/styles';
import { TEAMS_PATH } from '../TeamDetails/constants';
import { buildTeamColumns } from './columns';
import { mapApiTeam } from './mapApiTeam';
import { Team, TeamActionHandlers } from './types';
import { useDeleteTeam } from './useDeleteTeam';
import { useTeams } from './useTeams';

const COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  createdAt: false,
};

export function TeamsGrid({ onEdit, onDelete }: TeamActionHandlers) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    handleServerFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = useDiamGridPagination();

  const teamsQuery = useTeams({
    page: pageIndex,
    pageSize,
    sortModel,
    filterModel,
  });
  const deleteTeam = useDeleteTeam();
  const [teamPendingDeletion, setTeamPendingDeletion] = useState<Team | null>(
    null,
  );

  const rows = useMemo(
    () => (teamsQuery.data?.elements ?? []).map(team => mapApiTeam(team)),
    [teamsQuery.data],
  );

  useEffect(() => {
    if (teamsQuery.isError) {
      enqueueSnackbar(
        getApiErrorMessage(teamsQuery.error, 'Failed to load teams'),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, teamsQuery.error, teamsQuery.isError]);

  const handleRequestDelete = useCallback((team: Team) => {
    setTeamPendingDeletion(team);
  }, []);

  const handleCloseDelete = () => {
    setTeamPendingDeletion(null);
  };

  const handleConfirmDelete = async () => {
    if (!teamPendingDeletion) {
      return;
    }

    try {
      await deleteTeam.mutateAsync(teamPendingDeletion.id);
      onDelete?.(teamPendingDeletion);
      enqueueSnackbar(`Deleted ${teamPendingDeletion.name}`, {
        variant: 'success',
      });
      setTeamPendingDeletion(null);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to delete team'), {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () =>
      buildTeamColumns({
        onEdit,
        onDelete: handleRequestDelete,
      }),
    [handleRequestDelete, onEdit],
  );

  function handleRowClick(params: GridRowParams<Team>) {
    navigate(`${TEAMS_PATH}/${params.row.id}`);
  }

  return (
    <DataGridContainer elevation={1}>
      <DiamDataGrid
        rows={rows}
        columns={columns}
        loading={teamsQuery.isPending || teamsQuery.isFetching}
        columnVisibilityModel={COLUMN_VISIBILITY}
        disableRowSelectionOnClick
        getRowClassName={() => 'clickable'}
        onRowClick={handleRowClick}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleServerFilterModelChange}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        rowCount={teamsQuery.data?.total ?? 0}
        pageSizeOptions={[...DIAM_GRID_PAGE_SIZE_OPTIONS]}
        searchPlaceholder="Search teams"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />
      <Confirm
        isOpen={Boolean(teamPendingDeletion)}
        title="Delete team"
        content="You are going to delete the team. Members and child teams may be affected. Are you sure you want to proceed?"
        confirm="Yes"
        cancel="No"
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDelete}
      />
    </DataGridContainer>
  );
}
